import * as React from "react";
import { Box, CircularProgress, Modal, Stack, Typography } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import EventNoteIcon from "@mui/icons-material/EventNote";
import PersonIcon from "@mui/icons-material/Person";
import moment from "moment";
import { snakeCaseToTitleCase } from "../../../../utils/CaseFormatHelper";

interface AssignmentHistory {
  id: number;
  created_at: string;
  actor: string;
  description: string;
  notes: string | null;
}

interface Props {
  type: string;
  open: boolean;
  onCloseModal: () => void;
  histories: AssignmentHistory[] | undefined;
  loading?: boolean;
}

const rowLabelSx = {
  color: "#1E1E1E",
  fontSize: "13px",
  fontWeight: 500,
  lineHeight: "18px",
};

const cardBorderColor = (description: string): string => {
  if (description?.toLowerCase().includes("eligibility")) return "#FFDC68";
  if (description?.toLowerCase().includes("enrollment"))  return "#07BC2A";
  return "#FFDC68";
};

const parseDescription = (description: string): { heading: string; rows: string[] } => {
  const segments = description?.split(" | ") ?? [];
  const heading = segments[0]?.replace(/<[^>]*>/g, "").trim() || "Update";
  return { heading, rows: segments.slice(1) };
};

const renderFormattedText = (text: string): React.ReactNode => {
  if (!text) return text;

  const parts = text.split(/<b>(.*?)<\/b>/g);
  return parts.map((part, index) =>
    // Odd indices are always the captured <b>...</b> contents from String#split.
    index % 2 === 1 ? (
      <Box component="span" key={index} sx={{ fontWeight: 700 }}>
        {part}
      </Box>
    ) : (
      <React.Fragment key={index}>{part}</React.Fragment>
    )
  );
};

const AssignmentHistoryModal: React.FC<Props> = ({
  type,
  open,
  onCloseModal,
  histories = [],
  loading = false,
}) => {
  const title = `${snakeCaseToTitleCase(type)} Assignment History`;

  return (
    <Modal open={open} className="interventions-history-modal" onClose={onCloseModal}>
      <div
        className="paper"
        style={{ maxWidth: "920px", height: "88vh", borderRadius: "8px", overflow: "hidden" }}
      >
        <div className="paperInner">
          <Box>
            <Box className="main-header-container">
              <div
                className="main-header"
                style={{ marginBottom: "0px", borderBottom: "1px solid #E7E7E7" }}
              >
                <span>{title}</span>
                <button
                  id="dismiss-button"
                  onClick={onCloseModal}
                  type="button"
                  style={{ background: "transparent", border: 0, cursor: "pointer" }}
                >
                  <ClearIcon sx={{ width: 30, height: 30 }} />
                </button>
              </div>
            </Box>

            <div className="modal-content">
              {loading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                  <CircularProgress size={28} />
                </Box>
              )}

              {!loading && histories.length === 0 && (
                <Typography
                  sx={{ textAlign: "center", fontSize: "13px", fontWeight: 500, lineHeight: "18px", color: "#666" }}
                >
                  No history found.
                </Typography>
              )}

              {!loading && histories.length > 0 && (
                <Stack spacing={1} sx={{ maxHeight: "74vh", overflowY: "auto" }}>
                  {histories.map((history) => {
                    const { heading, rows } = parseDescription(history.description);

                    return (
                      <Box key={history.id} className="tile-container">
                        <Box
                          sx={{
                            width: "2px",
                            borderLeft: "2px solid",
                            borderColor: cardBorderColor(history.description),
                          }}
                        />
                        <Box>
                          <Typography className="tile-heading">{heading}</Typography>

                          <Stack direction="row" spacing={1.5} sx={{ mb: 1.25 }}>
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: "#8D8D8D" }}>
                              <EventNoteIcon sx={{ fontSize: 14 }} />
                              <Typography sx={{ fontFamily: "QuicksandMedium", color: "#8D8D8D", fontSize: "12px" }}>
                                {moment(history.created_at).format("MMMM D, YYYY")}{" "}
                                • {moment(history.created_at).format("h:mmA")}
                              </Typography>
                            </Stack>
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: "#8D8D8D" }}>
                              <PersonIcon sx={{ fontSize: 14 }} />
                              <Typography sx={{ fontFamily: "QuicksandMedium", color: "#8D8D8D", fontSize: "12px" }}>
                                {history.actor || "System"}
                              </Typography>
                            </Stack>
                          </Stack>

                          <Stack component="ul" sx={{ margin: 0, paddingLeft: "20px" }} spacing={0.3}>
                            {rows.map((row, index) => (
                              <Box component="li" key={index}>
                                <Typography sx={rowLabelSx}>
                                  {renderFormattedText(row)}
                                </Typography>
                              </Box>
                            ))}
                            {history.notes && (
                              <Box component="li">
                                <Typography sx={rowLabelSx}>
                                  <Box component="span" sx={rowLabelSx}>Notes:</Box>{" "}
                                  {renderFormattedText(history.notes)}
                                </Typography>
                              </Box>
                            )}
                          </Stack>
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </div>
          </Box>
        </div>
      </div>
    </Modal>
  );
};

export default AssignmentHistoryModal;
